import { type AnalyticsEvent } from '@autone/analytics';
import { useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { useDebouncedValue } from '../../../hooks';
import { type GetCoreLocationsRequest } from '../../../redux/services';
import { useGetCoreLocationsQuery } from '../../../redux/services/core';
import { TailwindFormField } from '../../../tailwind';
import { MultiSelectDropdown } from '../../multi-select-dropdown/MultiSelectDropdown';
import { MultiselectItemWithId } from '../CoreApiMultiselectItems';
import { useMultiselectApiFilters } from '../hooks';
import { type GetMultiselectMapper } from '../types';
import {
  getConcatenatedOptionId,
  getScopeItemFromConcatenatedId,
  SERVER_MIN_SEARCH_TERM_LENGTH,
  SERVER_SIDE_SEARCH_DROPDOWN_LIMIT,
} from '../utilities';

type LocationsFilters = GetCoreLocationsRequest['body']['filters'];

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  locationsMap?: GetMultiselectMapper<LocationsFilters['geographic'], T>;
  locationClustersMap?: GetMultiselectMapper<
    LocationsFilters['location_cluster'],
    T
  >;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreLocationsMultiselect = <T extends FieldValues>({
  control,
  name,
  locationsMap,
  locationClustersMap,
  disabled,
  analyticsEvent,
}: Props<T>) => {
  const geographicFilters = useMultiselectApiFilters({
    control,
    map: locationsMap,
    relevantFilterKeys: [
      'regions',
      'countries',
      'location_types',
      'regions',
      'territories',
    ],
  });
  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue({
    value:
      searchTerm.length < SERVER_MIN_SEARCH_TERM_LENGTH ? null : searchTerm,
    defaultValue: null,
  });

  const { data, isLoading } = useGetCoreLocationsQuery({
    query: { limit: SERVER_SIDE_SEARCH_DROPDOWN_LIMIT },
    body: {
      filters: {
        location_cluster: locationClustersFilters,
        location_groups: {},
        geographic: {
          search_term: debouncedSearchTerm,
          ...geographicFilters,
        },
      },
    },
  });

  const options = data?.items ?? [];

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled: fieldDisabled } }) => (
        <MultiSelectDropdown
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={getConcatenatedOptionId}
          getOptionLabel={(option) => option?.description}
          onSearchTermChange={setSearchTerm}
          selected={value?.map(getConcatenatedOptionId) ?? []}
          onSelectedChange={(ids) =>
            onChange(
              ids.length ? ids.map(getScopeItemFromConcatenatedId) : null,
            )
          }
          renderOption={(option) => <MultiselectItemWithId {...option} />}
          disabled={disabled || fieldDisabled}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
